import { useState } from 'react';
import QuestionFormModal from './QuestionFormModal';

export default function QuizEditor({ quiz = { title: '', questions: [] }, onChange }) {
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [isNewQuestion, setIsNewQuestion] = useState(false);

  const handleNewQuestion = () => {
    const newQ = {
      id: `q-${Date.now()}`,
      text: '',
      type: 'mcq',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
    };
    setEditingQuestion(newQ);
    setIsNewQuestion(true);
  };

  const handleSaveQuestion = (questionFromModal) => {
    const currentQuestions = quiz.questions || [];

    let updatedQuestions;
    if (isNewQuestion) {
      // Add new question
      updatedQuestions = [...currentQuestions, questionFromModal];
    } else {
      // Edit existing question - match using the ID that was passed to the modal
      updatedQuestions = currentQuestions.map(q => {
        // Match using either _id or id from the original question
        const originalId = q._id || q.id;
        const modalId = editingQuestion?.id;   // Use the ID from the editingQuestion we opened with

        return originalId === modalId ? questionFromModal : q;
      });
    }

    // Convert to backend format
    const backendQuestions = updatedQuestions.map(q => ({
      questionText: (q.text || q.questionText || '').trim(),
      options: Array.isArray(q.options) ? q.options : [],
      correctAnswer: Number(q.correctAnswer) ?? 0,
      explanation: (q.explanation || '').trim(),
    }));

    console.log(`Saving quiz - ${isNewQuestion ? 'NEW' : 'UPDATED'} question. Total questions: ${backendQuestions.length}`);

    onChange({
      ...quiz,
      title: quiz.title || "Lesson Quiz",
      questions: backendQuestions
    });

    setEditingQuestion(null);
    setIsNewQuestion(false);
  };

  const handleDeleteQuestion = (questionId) => {
    const currentQuestions = quiz.questions || [];
    const updatedQuestions = currentQuestions.filter(q => 
      (q._id || q.id) !== questionId
    );

    const backendQuestions = updatedQuestions.map(q => ({
      questionText: (q.questionText || q.text || '').trim(),
      options: Array.isArray(q.options) ? q.options : [],
      correctAnswer: Number(q.correctAnswer) ?? 0,
      explanation: (q.explanation || '').trim(),
    }));

    onChange({
      ...quiz,
      questions: backendQuestions
    });
  };

  // Normalize for display only
  const displayQuestions = (quiz.questions || []).map(q => ({
    id: q._id || q.id,
    text: q.questionText || q.text || '',
    type: q.type || 'mcq',
    options: q.options || [],
    correctAnswer: Number(q.correctAnswer) ?? 0,
    explanation: q.explanation || '',
  }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Quiz Questions</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {displayQuestions.length} question{displayQuestions.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleNewQuestion}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-teal-700 whitespace-nowrap transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          Add Question
        </button>
      </div>

      {displayQuestions.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-3">
            <i className="ri-question-mark text-xl text-slate-400" />
          </div>
          <p className="text-slate-600 font-medium text-sm">No questions yet</p>
          <p className="text-slate-400 text-xs mt-1 mb-4">Add MCQ or True/False questions to this quiz</p>
          <button
            onClick={handleNewQuestion}
            className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-teal-700 whitespace-nowrap"
          >
            Add First Question
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {displayQuestions.map((q, idx) => (
            <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-50 text-teal-600 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      q.type === 'mcq' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {q.type === 'mcq' ? 'MCQ' : 'True/False'}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800">{q.text || 'No question text'}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Correct answer: <span className="text-emerald-600 font-medium">
                      {q.options?.[q.correctAnswer] || 'Not set'}
                    </span>
                  </p>
                  {q.explanation && (
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{q.explanation}</p>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => { 
                      setEditingQuestion(q); 
                      setIsNewQuestion(false); 
                    }}
                    className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-200 cursor-pointer whitespace-nowrap"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(q.id)}
                    className="px-3 py-1.5 bg-rose-50 text-rose-500 text-xs font-medium rounded-lg hover:bg-rose-100 cursor-pointer whitespace-nowrap"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingQuestion && (
        <QuestionFormModal
          question={editingQuestion}
          isNew={isNewQuestion}
          onSave={handleSaveQuestion}
          onClose={() => { 
            setEditingQuestion(null); 
            setIsNewQuestion(false); 
          }}
        />
      )}
    </div>
  );
}